<script lang="ts" setup>
  import { storeToRefs } from 'pinia'
  import {
    ContentBlockLearnSnackData,
    ContentBlockTypeId,
  } from '~/model/ContentBlockData/ContentBlockData'
  import { UseBemProps } from '~/composables/Bem/BemFacetOptions'
  import useBem from '~/composables/Bem/Bem'
  import { GameDistrictContent, GameDistrictId } from '~/model/GameDistrict/GameDistrict'
  import { IconBoxedFacet } from '~/components/IconBoxed/IconBoxedFacet'
  import { VisualThemeId } from '~/model/VisualTheme/VisualTheme'
  import { useLocaleStore } from '~/store/Locale'
  import { useGameContentStore } from '~/store/GameContent'
  import { ref, watch } from '#imports'
  import useGameDistrict from '~/composables/GameDistrict/GameDistrict'
  import useTranslation from '~/composables/Translation/Translation'
  import { OverlayComponent, OverlayId } from '~/model/Overlay/Overlay'
  import { UiOverlayFacet } from '~/components/Ui/UiOverlay/UiOverlayFacet'
  import { useOverlayStore } from '~/store/Overlay'

  interface Props extends UseBemProps, ContentBlockLearnSnackData {
    type: ContentBlockTypeId
    facets?: Array<string>
    districtId: GameDistrictId
  }

  const localeStore = useLocaleStore()
  const { assignPendingData } = useGameContentStore()
  const { openOverlay } = useOverlayStore()

  const props = defineProps<Props>()
  const { localeId } = storeToRefs(localeStore)
  const { t } = useTranslation()
  const { bemFacets } = useBem('c-content-block-table', props, {})
  const { getContent } = useGameDistrict()

  const districtContent = ref<GameDistrictContent | null>(null)

  const onRequestOverlay = () => {
    openOverlay(OverlayId.FullScreen, {
      transition: 'trs-simple-fade',
      facets: [UiOverlayFacet.FullScreen],
      closeWithKey: 'Escape',
      component: OverlayComponent.OverlayContentLearnSnack,
      disableScroll: true,
      props: {},
    })
  }

  watch(
    () => props.districtId,
    async (newVal) => {
      if (!newVal) {
        return
      }

      const req = await getContent(localeId.value, props.districtId)

      assignPendingData<GameDistrictContent>(req, (data) => {
        districtContent.value = data.value
      })
    },
    { immediate: true },
  )
</script>

<template>
  <button :class="bemFacets" class="c-content-block-learn-snack" @click="onRequestOverlay">
    <div class="c-content-block-learn-snack__box btn btn--mono-black">
      <div class="c-content-block-learn-snack__icon">
        <IconBoxed
          :facets="[IconBoxedFacet.XXXLarge]"
          :visual-theme="VisualThemeId.Negative"
          class="c-content-block-learn-snack__symbol"
        >
          <template #letter>
            {{ 'L' }}
          </template>
        </IconBoxed>
      </div>
      <div class="c-content-block-learn-snack__title">
        <span class="u-reset u-reset--h4"> {{ t('content-block-learn-snack.title') }}: </span>
        <h2 class="c-content-block-learn-snack__legend">
          {{ districtContent.name }}
        </h2>
      </div>
    </div>
  </button>
</template>

<style lang="scss" scoped>
  @use '@nirazul/scss-utils' as utils;
  @use '/assets/scss/util/color/color' as col;
  @use '/assets/scss/util/transition' as trs;
  @use '/assets/scss/util/anaglyph/anaglyph' as anaglyph;

  $offset: 4px;

  .c-content-block-learn-snack {
    overflow: visible;
    grid-column: 4 / 10;
  }

  .c-content-block-learn-snack__box {
    display: flex;
    flex-flow: row nowrap;
    column-gap: 24px;
    padding: 16px;
    margin: 0 $offset $offset 0;
    transition: box-shadow trs.$default, transform trs.$default, background-color trs.$default;

    @include utils.has-focus {
      background-color: col.$brand-cyan;
    }
  }

  .c-content-block-learn-snack__icon {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 0 0 88px;
    height: 88px;
  }

  .c-content-block-learn-snack__title {
    display: flex;
    flex: 0 1 100%;
    flex-flow: column nowrap;
    justify-content: center;
    text-align: left;
  }

  .c-content-block-learn-snack__legend {
    text-align: left;
    transition: color trs.$default;
    text-decoration: underline;
  }
</style>
